import { useMemo } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Badge, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { usePulseDecisions } from '../PulseSecurity/usePulseSecurity';

/**
 * CrowdSec-Statistiken — aggregiert die von Pulse gespiegelten aktiven
 * CrowdSec-Decisions (GET /v1/decisions) clientseitig zu Kennzahlen:
 * Gesamt-Bans, Verteilung nach Szenario / Type / Origin.
 *
 * Bewusst read-only + ohne neuen Backend-Endpoint — nutzt die bereits
 * vorhandene Decisions-Spiegelung. Sobald Pulse ein natives
 * /v1/crowdsec/metrics bekommt (LAPI `cscli metrics`), kann diese Seite
 * darauf umgestellt werden.
 */
function countBy(items, key) {
  const map = new Map();
  for (const it of items) {
    const k = it[key] || 'unbekannt';
    map.set(k, (map.get(k) || 0) + 1);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function StatCard({ title, value, color = 'primary' }) {
  return (
    <Card className="mb-3">
      <CardBody className="text-center">
        <div className={`text-${color}`} style={{ fontSize: '2rem', fontWeight: 700 }}>{value}</div>
        <div className="text-muted small">{title}</div>
      </CardBody>
    </Card>
  );
}

function DistributionTable({ title, rows }) {
  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h6">{title}</CardTitle>
        <DataTable empty={rows.length === 0} emptyMessage="Keine Daten.">
          <thead><tr><th>Wert</th><th style={{ width: 90 }} className="text-end">Anzahl</th></tr></thead>
          <tbody>
            {rows.map(([k, n]) => (
              <tr key={k}>
                <td className="small font-monospace">{k}</td>
                <td className="text-end"><Badge color="secondary" pill>{n}</Badge></td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </CardBody>
    </Card>
  );
}

export function PulseCrowdsecPage() {
  const query = usePulseDecisions({ activeOnly: true, limit: 500 });
  const items = query.data?.items ?? [];

  const stats = useMemo(() => ({
    total: items.length,
    bans: items.filter((d) => d.type === 'ban').length,
    captcha: items.filter((d) => d.type === 'captcha').length,
    byScenario: countBy(items, 'scenario'),
    byType: countBy(items, 'type'),
    byOrigin: countBy(items, 'origin'),
  }), [items]);

  return (
    <div>
      <PageHeader title="CrowdSec · Statistiken" subtitle="Aktive Decisions aggregiert · Auto-Refresh 30s" />
      <Alert color="info" className="small">
        Kennzahlen aus den aktiven CrowdSec-Decisions, die Pulse aus der LAPI
        spiegelt. Für die IP-Freigabe der exponierten CrowdSec-API (<code>cs.api.valitype.net</code>)
        siehe Nexus · Dedicated / NetBox-Allowlist.
      </Alert>

      {query.error && <Alert color="danger" className="small">{query.error.message}</Alert>}

      <Row>
        <Col md={4}><StatCard title="Aktive Decisions" value={stats.total} color="primary" /></Col>
        <Col md={4}><StatCard title="Bans" value={stats.bans} color="danger" /></Col>
        <Col md={4}><StatCard title="Captcha/Throttle" value={stats.captcha} color="warning" /></Col>
      </Row>

      <Row>
        <Col md={4}><DistributionTable title="Nach Szenario" rows={stats.byScenario} /></Col>
        <Col md={4}><DistributionTable title="Nach Type" rows={stats.byType} /></Col>
        <Col md={4}><DistributionTable title="Nach Origin" rows={stats.byOrigin} /></Col>
      </Row>
    </div>
  );
}
